import { type RelationShare } from '@/types/trpc/hoa';
import { type ShareValues } from '@/containers/Finance/Invoices/hoa/shared/types/formTypes';
import { type UnitRelationOptionInput } from './RelationDivisionLine/RelationDivisionLine.types';

import { DivisionType } from '~/common/enums';
import listUtils from '@/shared/utils/list';

import {
  convertPercentageToCommonFraction,
  percentagesToCommonDenominator,
} from '@/shared/utils/mathUtils';
import {
  splitAmountByFractions,
  splitPercentageByFractions,
  toDivisionPercentage,
} from './RelationDivisionLine/RelationDivisionLine.utils';

type SharePercentageInput = { percentage: number | null };

const createEmptyShare = (): ShareValues => ({
  amount: null,
  buildingRelationId: null,
  percentage: null,
  share: null,
  unitId: null,
});

type ShareRelationInput = Pick<RelationShare, 'companyId' | 'contactId'>;

export type ToUpdateShareValues = {
  amount: number | null;
  percentage: number | null;
};

const findUnitRelationOption = <T extends UnitRelationOptionInput>(
  options: readonly T[],
  share: ShareRelationInput,
) =>
  options.find((option) =>
    share.contactId
      ? option.data.contactId === share.contactId
      : !option.data.contactId && option.data.companyId === share.companyId,
  );

const toPercentageNumber = (percentage: ShareValues['percentage']) => Number(percentage) || 0;

type DivideByRelationSharesInput<T extends UnitRelationOptionInput> = {
  relationShares: RelationShare[];
  totalAmount: number;
  unitRelationOptions: readonly T[];
};

const divideByRelationShares = <T extends UnitRelationOptionInput>({
  relationShares,
  totalAmount,
  unitRelationOptions,
}: DivideByRelationSharesInput<T>): ShareValues[] => {
  const matchedShares: { option: T; shares: number }[] = [];

  let totalShares = 0;

  relationShares.forEach((relationShare) => {
    const option = findUnitRelationOption(unitRelationOptions, relationShare);

    if (!option) return;

    matchedShares.push({ option, shares: relationShare.shares });

    totalShares += relationShare.shares;
  });

  if (!totalShares) return [];

  const fractions = matchedShares.map((res) => res.shares / totalShares);

  const amounts = splitAmountByFractions(totalAmount, fractions);
  const percentages = splitPercentageByFractions(fractions);

  return matchedShares.map((match, index) => {
    const { option, shares } = match;

    return {
      amount: amounts[index] ?? 0,
      buildingRelationId: option.value.buildingRelationId,
      percentage: percentages[index] ?? 0,
      share: shares,
      unitId: option.value.unitId,
    };
  });
};

const getPercentageTotal = <T extends SharePercentageInput>(
  divisions: T[],
  predicate?: (division: T, index: number) => boolean,
) =>
  divisions.reduce(
    (total, division, index) =>
      !!predicate && !predicate(division, index)
        ? total
        : total + toPercentageNumber(division.percentage),
    0,
  );

const withRecalculatedShares = <T extends SharePercentageInput>(shares: readonly T[]) => {
  const sharePercentages = shares.map((share) => toPercentageNumber(share.percentage));

  const commonDenominator = percentagesToCommonDenominator(sharePercentages);

  return shares.map((share) => {
    const percentage = toPercentageNumber(share.percentage);

    const [numerator] = convertPercentageToCommonFraction(percentage, commonDenominator);

    return { ...share, share: numerator };
  });
};

const updateShareAt = <T extends ToUpdateShareValues>(
  shares: readonly T[],
  sharePosition: number | ((item: T) => boolean),
  toUpdateValues: ToUpdateShareValues,
) => {
  const nextShares = listUtils.replace(
    shares,
    (prevShare) => ({
      ...prevShare,
      amount: toUpdateValues.amount,
      percentage: toUpdateValues.percentage,
    }),
    sharePosition,
  );

  return withRecalculatedShares(nextShares);
};

type WithDerivedValuesInput<T extends ToUpdateShareValues> = {
  divisionType: DivisionType | null;
  shares: T[];
  totalAmount: number;
};

// percentage => amounts, amount => percentages and shares
const withDerivedValues = <T extends ToUpdateShareValues>({
  divisionType,
  shares,
  totalAmount,
}: WithDerivedValuesInput<T>): T[] => {
  if (divisionType === DivisionType.Percentage) {
    const fractions = shares.map((share) => toPercentageNumber(share.percentage) / 100);

    const amounts = splitAmountByFractions(totalAmount, fractions);

    return shares.map((share, index) => ({ ...share, amount: amounts[index] ?? 0 }));
  }

  const nextShares = shares.map((share) => {
    const shareAmount = share.amount ?? 0;

    return {
      ...share,
      percentage: totalAmount ? toDivisionPercentage(shareAmount / totalAmount) : 0,
    };
  });

  return withRecalculatedShares(nextShares);
};

const internal = {
  findUnitRelationOption,
  toPercentageNumber,
  withRecalculatedShares,
};

export {
  createEmptyShare,
  divideByRelationShares,
  getPercentageTotal,
  internal,
  updateShareAt,
  withDerivedValues,
};
